<template>
  <div class="personnel-award-container">
    <!-- Hero Header Section -->
    <v-card class="hero-header mb-6" elevation="0">
      <div class="hero-content">
        <div class="hero-icon-wrapper">
          <v-icon size="48" color="white">mdi-trophy-award</v-icon>
        </div>
        <div class="hero-text">
          <h1 class="hero-title">ผลงาน / รางวัล / เชิดชูเกียรติ</h1>
          <p class="hero-subtitle">2.1 ผลงานที่เกิดจากการปฏิบัติงานในสถานศึกษาปัจจุบัน</p>
        </div>
        <v-spacer></v-spacer>
        <v-btn class="add-btn" large depressed @click="personnel_awardAdd()">
          <v-icon left>mdi-plus</v-icon>
          เพิ่มผลงาน
        </v-btn>
      </div>
    </v-card>

    <!-- Statistics Cards -->
    <v-row class="stats-row mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-1" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-medal</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ personnel_awards.length }}</span>
            <span class="stat-label">ผลงานทั้งหมด</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-2" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-trophy</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ countByLevel('nation') + countByLevel('word') }}</span>
            <span class="stat-label">ระดับชาติ/โลก</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-3" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-lightbulb-on</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ countByType('innovation') }}</span>
            <span class="stat-label">สิ่งประดิษฐ์</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-4" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-star-circle</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ countByType('award') }}</span>
            <span class="stat-label">เชิดชูเกียรติ</span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Data Table Card -->
    <v-card class="main-table-card" elevation="0">
      <v-card-title class="table-header">
        <div class="header-left">
          <v-icon class="mr-3" color="#dc2626">mdi-format-list-bulleted-type</v-icon>
          <span class="header-title">รายการผลงาน / รางวัล</span>
        </div>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="ค้นหาผลงาน..."
          single-line
          hide-details
          dense
          outlined
          class="search-field"
          style="max-width: 350px"
        ></v-text-field>
      </v-card-title>

      <v-divider></v-divider>

      <v-data-table
        :loading="loading"
        :headers="headers"
        :items="personnel_awards"
        :search="search"
        :items-per-page="10"
        class="modern-table"
        loading-text="กำลังโหลดข้อมูล..."
        no-data-text="ไม่พบข้อมูลผลงาน/รางวัล"
        no-results-text="ไม่พบผลลัพธ์ที่ค้นหา"
      >
        <template v-slot:item="{ item, index }">
          <tr class="table-row">
            <td class="text-center">
              <v-avatar size="32" color="#dc2626" class="white--text font-weight-bold">
                {{ index + 1 }}
              </v-avatar>
            </td>
            <td class="text-center">
              <v-chip small color="#fef2f2" text-color="#dc2626" class="year-chip">
                <v-icon left small>mdi-calendar</v-icon>
                {{ item.personnel_awardYear }}
              </v-chip>
            </td>
            <td>
              <div class="performance-cell">
                <v-icon small color="#f97316" class="mr-2">mdi-trophy-variant</v-icon>
                <span class="performance-text">{{ item.performance_name }}</span>
              </div>
            </td>
            <td>
              <div class="location-cell">
                <v-icon small color="#0ea5e9" class="mr-1">mdi-map-marker</v-icon>
                {{ item.performance_location }}
              </div>
            </td>
            <td class="text-center">
              <v-chip small color="#f0fdf4" text-color="#16a34a">
                <v-icon left small>mdi-calendar-clock</v-icon>
                {{ item.performance_time | moment("add", "543 years") | moment("D MMM YYYY") }}
              </v-chip>
            </td>
            <td class="text-center">
              <v-chip
                small
                :color="getLevelColor(item.award_level)"
                dark
                class="level-chip"
              >
                <v-icon left small>{{ getLevelIcon(item.award_level) }}</v-icon>
                {{ getLevelText(item.award_level) }}
              </v-chip>
            </td>
            <td class="text-center">
              <v-chip
                small
                :color="getTypeColor(item.type_performance)"
                outlined
                class="type-chip"
              >
                <v-icon left small>{{ getTypeIcon(item.type_performance) }}</v-icon>
                {{ getTypeText(item.type_performance, item.personnel_awardEtc) }}
              </v-chip>
            </td>
            <td class="text-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    class="action-btn edit-btn mr-1"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="personnel_awardEdit(item.id_pa)"
                  >
                    <v-icon small>mdi-pencil-outline</v-icon>
                  </v-btn>
                </template>
                <span>แก้ไข</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    class="action-btn delete-btn"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="personnel_awardDelete(item.id_pa)"
                  >
                    <v-icon small>mdi-delete-outline</v-icon>
                  </v-btn>
                </template>
                <span>ลบ</span>
              </v-tooltip>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <!-- Add Award Dialog -->
    <v-dialog
      v-model="addpersonnel_awarddialog"
      persistent
      max-width="700px"
      content-class="modern-dialog"
    >
      <v-card class="dialog-card">
        <v-card-title class="dialog-header add-header">
          <v-icon class="mr-3" color="white" size="28">mdi-plus-circle-outline</v-icon>
          <span>เพิ่มผลงาน / รางวัล</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="addpersonnel_awarddialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="dialog-content">
          <v-form ref="addpersonnel_awardform" lazy-validation>
            <!-- Section: ข้อมูลผลงาน -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#dc2626" class="mr-2">mdi-trophy-award</v-icon>
                ข้อมูลผลงาน / รางวัล
              </div>
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="addpersonnel_award.performance_name"
                    label="ชื่อผลงาน / รางวัล"
                    placeholder="ระบุชื่อผลงานหรือรางวัลที่ได้รับ"
                    :rules="[v => !!v || 'กรุณาระบุผลงาน']"
                    outlined
                    rows="2"
                    auto-grow
                    prepend-inner-icon="mdi-trophy-variant"
                    class="modern-input"
                  ></v-textarea>
                </v-col>
              </v-row>
            </div>

            <!-- Section: ช่วงเวลาและสถานที่ -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#0ea5e9" class="mr-2">mdi-calendar-clock</v-icon>
                ช่วงเวลาและสถานที่
              </div>
              <v-row>
                <v-col cols="12" md="6">
                  <v-autocomplete
                    :items="yearSelect"
                    item-text="text"
                    item-value="value"
                    v-model="addpersonnel_award.personnel_awardYear"
                    label="ปีผลงาน (พ.ศ.)"
                    :rules="[v => !!v || 'กรุณาเลือกปี']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-calendar"
                    class="modern-input"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="6">
                  <v-menu
                    ref="menu2"
                    v-model="menu2"
                    :close-on-content-click="false"
                    :return-value.sync="date"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="addpersonnel_award.performance_time"
                        label="วันที่ได้รับ"
                        prepend-inner-icon="mdi-calendar-check"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        dense
                        class="modern-input"
                        hint="คลิกเพื่อเลือกวันที่"
                        persistent-hint
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="addpersonnel_award.performance_time"
                      no-title
                      scrollable
                      locale="th-TH"
                      color="#dc2626"
                    >
                      <v-spacer></v-spacer>
                      <v-btn text color="grey" @click="menu2 = false">ยกเลิก</v-btn>
                      <v-btn text color="#dc2626" @click="$refs.menu2.save(date)">ตกลง</v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="addpersonnel_award.performance_location"
                    label="สถานที่จัดงาน"
                    placeholder="เช่น ศูนย์ประชุมแห่งชาติสิริกิติ์"
                    :rules="[v => !!v || 'กรุณาระบุสถานที่']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-map-marker"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>

            <!-- Section: ระดับและประเภท -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#f97316" class="mr-2">mdi-star-circle</v-icon>
                ระดับและประเภทผลงาน
              </div>
              <v-row>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="addpersonnel_award.award_level"
                    :items="award_levels"
                    item-text="text"
                    item-value="value"
                    label="ระดับผลงาน"
                    :rules="[v => !!v || 'กรุณาเลือกระดับ']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-trophy"
                    class="modern-input"
                  >
                    <template v-slot:item="{ item }">
                      <v-icon class="mr-2" :color="getLevelColor(item.value)" small>
                        {{ getLevelIcon(item.value) }}
                      </v-icon>
                      {{ item.text }}
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="addpersonnel_award.type_performance"
                    :items="type_performances"
                    item-text="text"
                    item-value="value"
                    label="ประเภทผลงาน"
                    :rules="[v => !!v || 'กรุณาเลือกประเภท']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-tag"
                    class="modern-input"
                  >
                    <template v-slot:item="{ item }">
                      <v-icon class="mr-2" :color="getTypeColor(item.value)" small>
                        {{ getTypeIcon(item.value) }}
                      </v-icon>
                      {{ item.text }}
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" v-if="addpersonnel_award.type_performance === 'etc'">
                  <v-textarea
                    v-model="addpersonnel_award.personnel_awardEtc"
                    label="ประเภทอื่นๆ (โปรดระบุ)"
                    placeholder="ระบุรายละเอียดประเภทผลงาน"
                    :rules="[v => !!v || 'กรุณาระบุรายละเอียด']"
                    outlined
                    rows="2"
                    prepend-inner-icon="mdi-text-box-outline"
                    class="modern-input"
                  ></v-textarea>
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text large @click="addpersonnel_awarddialog = false" class="cancel-btn">
            <v-icon left>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            large
            depressed
            color="#dc2626"
            dark
            @click="addpersonnel_awardSubmit()"
            class="save-btn"
          >
            <v-icon left>mdi-content-save-outline</v-icon>
            บันทึกข้อมูล
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Award Dialog -->
    <v-dialog
      v-model="editpersonnel_awarddialog"
      persistent
      max-width="700px"
      content-class="modern-dialog"
    >
      <v-card class="dialog-card">
        <v-card-title class="dialog-header edit-header">
          <v-icon class="mr-3" color="white" size="28">mdi-pencil-outline</v-icon>
          <span>แก้ไขผลงาน / รางวัล</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="editpersonnel_awarddialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="dialog-content">
          <v-form ref="editpersonnel_awardform" lazy-validation>
            <!-- Section: ข้อมูลผลงาน -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#f97316" class="mr-2">mdi-trophy-award</v-icon>
                ข้อมูลผลงาน / รางวัล
              </div>
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="editpersonnel_award.performance_name"
                    label="ชื่อผลงาน / รางวัล"
                    :rules="[v => !!v || 'กรุณาระบุผลงาน']"
                    outlined
                    rows="2"
                    auto-grow
                    prepend-inner-icon="mdi-trophy-variant"
                    class="modern-input"
                  ></v-textarea>
                </v-col>
              </v-row>
            </div>

            <!-- Section: ช่วงเวลาและสถานที่ -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#0ea5e9" class="mr-2">mdi-calendar-clock</v-icon>
                ช่วงเวลาและสถานที่
              </div>
              <v-row>
                <v-col cols="12" md="6">
                  <v-autocomplete
                    :items="yearSelect"
                    item-text="text"
                    item-value="value"
                    v-model="editpersonnel_award.personnel_awardYear"
                    label="ปีผลงาน (พ.ศ.)"
                    :rules="[v => !!v || 'กรุณาเลือกปี']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-calendar"
                    class="modern-input"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="6">
                  <v-menu
                    ref="menu3"
                    v-model="menu3"
                    :close-on-content-click="false"
                    :return-value.sync="date"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="editpersonnel_award.performance_time"
                        label="วันที่ได้รับ"
                        prepend-inner-icon="mdi-calendar-check"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        dense
                        class="modern-input"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="editpersonnel_award.performance_time"
                      no-title
                      scrollable
                      locale="th-TH"
                      color="#f97316"
                    >
                      <v-spacer></v-spacer>
                      <v-btn text color="grey" @click="menu3 = false">ยกเลิก</v-btn>
                      <v-btn text color="#f97316" @click="$refs.menu3.save(date)">ตกลง</v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="editpersonnel_award.performance_location"
                    label="สถานที่จัดงาน"
                    :rules="[v => !!v || 'กรุณาระบุสถานที่']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-map-marker"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>

            <!-- Section: ระดับและประเภท -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#16a34a" class="mr-2">mdi-star-circle</v-icon>
                ระดับและประเภทผลงาน
              </div>
              <v-row>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="editpersonnel_award.award_level"
                    :items="award_levels"
                    item-text="text"
                    item-value="value"
                    label="ระดับผลงาน"
                    :rules="[v => !!v || 'กรุณาเลือกระดับ']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-trophy"
                    class="modern-input"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="editpersonnel_award.type_performance"
                    :items="type_performances"
                    item-text="text"
                    item-value="value"
                    label="ประเภทผลงาน"
                    :rules="[v => !!v || 'กรุณาเลือกประเภท']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-tag"
                    class="modern-input"
                  ></v-select>
                </v-col>
                <v-col cols="12" v-if="editpersonnel_award.type_performance === 'etc'">
                  <v-textarea
                    v-model="editpersonnel_award.personnel_awardEtc"
                    label="ประเภทอื่นๆ (โปรดระบุ)"
                    :rules="[v => !!v || 'กรุณาระบุรายละเอียด']"
                    outlined
                    rows="2"
                    prepend-inner-icon="mdi-text-box-outline"
                    class="modern-input"
                  ></v-textarea>
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text large @click="editpersonnel_awarddialog = false" class="cancel-btn">
            <v-icon left>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            large
            depressed
            color="#f97316"
            dark
            @click="editpersonnel_awardSubmit()"
            class="save-btn"
          >
            <v-icon left>mdi-content-save-outline</v-icon>
            บันทึกการแก้ไข
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog
      v-model="deletepersonnel_awarddialog"
      persistent
      max-width="500px"
      content-class="modern-dialog"
    >
      <v-card class="dialog-card delete-dialog">
        <v-card-title class="dialog-header delete-header">
          <v-icon class="mr-3" color="white" size="28">mdi-delete-alert-outline</v-icon>
          <span>ยืนยันการลบข้อมูล</span>
        </v-card-title>

        <v-card-text class="delete-content text-center">
          <v-form ref="deletepersonnel_awardform" lazy-validation>
            <div class="delete-icon-wrapper">
              <v-icon size="72" color="#dc2626">mdi-alert-circle-outline</v-icon>
            </div>
            <div class="delete-message">คุณต้องการลบผลงาน/รางวัลนี้หรือไม่?</div>

            <v-card class="delete-info-card mt-4" outlined>
              <v-card-text class="pa-4">
                <div class="delete-info-item">
                  <v-icon small color="#f97316" class="mr-2">mdi-trophy-variant</v-icon>
                  <span class="info-text">{{ editpersonnel_award.performance_name }}</span>
                </div>
                <v-divider class="my-3"></v-divider>
                <div class="delete-info-row">
                  <v-chip small color="#fef2f2" text-color="#dc2626" class="mr-2">
                    <v-icon left small>mdi-map-marker</v-icon>
                    {{ editpersonnel_award.performance_location }}
                  </v-chip>
                </div>
              </v-card-text>
            </v-card>

            <div class="delete-warning mt-4">
              <v-icon small color="#f97316" class="mr-1">mdi-alert</v-icon>
              การดำเนินการนี้ไม่สามารถย้อนกลับได้
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text large @click="deletepersonnel_awarddialog = false" class="cancel-btn">
            <v-icon left>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            large
            depressed
            color="#dc2626"
            dark
            @click="deletepersonnel_awardubmit()"
            class="delete-confirm-btn"
          >
            <v-icon left>mdi-delete-outline</v-icon>
            ลบข้อมูล
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      top
      right
      class="modern-snackbar"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-3" color="white">{{ snackbar.icon }}</v-icon>
        <span>{{ snackbar.text }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">ปิด</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: 'PersonnelAward',

  data() {
    return {
      ApiKey: 'HRvec2021',
      loading: true,
      search: '',
      addpersonnel_awarddialog: false,
      editpersonnel_awarddialog: false,
      deletepersonnel_awarddialog: false,
      snackbar: {
        show: false,
        color: '',
        timeout: 4000,
        icon: '',
        text: ''
      },
      headers: [
        { text: '#', align: 'center', value: 'index', width: '60px', sortable: false },
        { text: 'ปี', align: 'center', value: 'personnel_awardYear', width: '100px' },
        { text: 'ผลงาน / รางวัล', align: 'left', value: 'performance_name' },
        { text: 'สถานที่', align: 'left', value: 'performance_location', width: '180px' },
        { text: 'วันที่', align: 'center', value: 'performance_time', width: '130px' },
        { text: 'ระดับ', align: 'center', value: 'award_level', width: '160px' },
        { text: 'ประเภท', align: 'center', value: 'type_performance', width: '150px' },
        { text: 'จัดการ', align: 'center', value: 'actions', sortable: false, width: '100px' }
      ],
      personnel_awards: [],
      addpersonnel_award: {},
      editpersonnel_award: {},
      date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      menu2: false,
      menu3: false,
      award_levels: [
        { text: 'ระดับวิทยาลัย', value: 'college' },
        { text: 'ระดับสำนักงานอาชีวศึกษาจังหวัด', value: 'provincial' },
        { text: 'ระดับภาค', value: 'sector' },
        { text: 'ระดับอาชีวศึกษา', value: 'vocational' },
        { text: 'ระดับชาติ', value: 'nation' },
        { text: 'ระดับโลก', value: 'word' }
      ],
      type_performances: [
        { text: 'การแข่งขันทักษะ', value: 'skill' },
        { text: 'การประกวดสิ่งประดิษฐ์', value: 'innovation' },
        { text: 'รางวัลเชิดชูเกียรติ', value: 'award' },
        { text: 'อื่นๆ', value: 'etc' }
      ],
      yearSelect: [
        { text: '2560', value: '2560' },
        { text: '2561', value: '2561' },
        { text: '2562', value: '2562' },
        { text: '2563', value: '2563' },
        { text: '2564', value: '2564' },
        { text: '2565', value: '2565' },
        { text: '2566', value: '2566' },
        { text: '2567', value: '2567' },
        { text: '2568', value: '2568' },
        { text: '2569', value: '2569' },
        { text: '2570', value: '2570' }
      ]
    }
  },

  async mounted() {
    await this.personnel_awardsQueryAll()
  },

  methods: {
    // Helper methods
    countByLevel(level) {
      return this.personnel_awards.filter(item => item.award_level === level).length
    },

    countByType(type) {
      return this.personnel_awards.filter(item => item.type_performance === type).length
    },

    getLevelColor(level) {
      const colors = {
        college: '#64748b',
        provincial: '#0ea5e9',
        sector: '#8b5cf6',
        vocational: '#f97316',
        nation: '#dc2626',
        word: '#eab308'
      }
      return colors[level] || '#6b7280'
    },

    getLevelIcon(level) {
      const icons = {
        college: 'mdi-school',
        provincial: 'mdi-office-building',
        sector: 'mdi-map',
        vocational: 'mdi-certificate',
        nation: 'mdi-flag',
        word: 'mdi-earth'
      }
      return icons[level] || 'mdi-trophy'
    },

    getLevelText(level) {
      const texts = {
        college: 'วิทยาลัย',
        provincial: 'อศจ.',
        sector: 'ภาค',
        vocational: 'อาชีวะ',
        nation: 'ชาติ',
        word: 'โลก'
      }
      return texts[level] || level
    },

    getTypeColor(type) {
      const colors = {
        skill: '#0ea5e9',
        innovation: '#16a34a',
        award: '#f97316',
        etc: '#6b7280'
      }
      return colors[type] || '#6b7280'
    },

    getTypeIcon(type) {
      const icons = {
        skill: 'mdi-account-star',
        innovation: 'mdi-lightbulb-on',
        award: 'mdi-medal',
        etc: 'mdi-dots-horizontal'
      }
      return icons[type] || 'mdi-tag'
    },

    getTypeText(type, etc) {
      const texts = {
        skill: 'แข่งขันทักษะ',
        innovation: 'สิ่งประดิษฐ์',
        award: 'เชิดชูเกียรติ',
        etc: etc || 'อื่นๆ'
      }
      return texts[type] || type
    },

    // API methods
    async personnel_awardsQueryAll() {
      this.loading = true
      let userSession = JSON.parse(localStorage.getItem('user')) || 0
      let result = await this.$http
        .post('personnel_award.php', {
          ApiKey: this.ApiKey,
          personnel_awardid_card: userSession.id_card
        })
        .finally(() => (this.loading = false))
      this.personnel_awards = result.data
    },

    // Add operations
    personnel_awardAdd() {
      this.addpersonnel_award = {}
      this.addpersonnel_awarddialog = true
    },

    async addpersonnel_awardSubmit() {
      if (this.$refs.addpersonnel_awardform.validate()) {
        let userSession = JSON.parse(localStorage.getItem('user')) || 0
        this.addpersonnel_award.ApiKey = this.ApiKey
        this.addpersonnel_award.personnel_awardid_card = userSession.id_card

        let result = await this.$http.post(
          'personnel_award.insert.php',
          this.addpersonnel_award
        )

        if (result.data.status === true) {
          Swal.fire({
            icon: 'success',
            title: 'บันทึกสำเร็จ',
            text: 'เพิ่มผลงาน/รางวัลเรียบร้อยแล้ว',
            showConfirmButton: false,
            timer: 1500
          })
          this.personnel_awardsQueryAll()
        } else {
          Swal.fire({
            icon: 'error',
            title: 'เกิดข้อผิดพลาด',
            text: 'ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง',
            showConfirmButton: true
          })
        }
        this.addpersonnel_awarddialog = false
      }
    },

    // Edit operations
    async personnel_awardEdit(id_pa) {
      let result = await this.$http.post('personnel_award.php', {
        ApiKey: this.ApiKey,
        id_pa: id_pa
      })
      this.editpersonnel_award = result.data
      this.editpersonnel_awarddialog = true
    },

    async editpersonnel_awardSubmit() {
      if (this.$refs.editpersonnel_awardform.validate()) {
        this.editpersonnel_award.ApiKey = this.ApiKey

        let result = await this.$http.post(
          'personnel_award.update.php',
          this.editpersonnel_award
        )

        if (result.data.status === true) {
          Swal.fire({
            icon: 'success',
            title: 'แก้ไขสำเร็จ',
            text: 'อัปเดตผลงาน/รางวัลเรียบร้อยแล้ว',
            showConfirmButton: false,
            timer: 1500
          })
          this.personnel_awardsQueryAll()
        } else {
          Swal.fire({
            icon: 'error',
            title: 'เกิดข้อผิดพลาด',
            text: 'ไม่สามารถแก้ไขข้อมูลได้ กรุณาลองใหม่อีกครั้ง',
            showConfirmButton: true
          })
        }
        this.editpersonnel_awarddialog = false
      }
    },

    // Delete operations
    async personnel_awardDelete(id_pa) {
      let result = await this.$http.post('personnel_award.php', {
        ApiKey: this.ApiKey,
        id_pa: id_pa
      })
      this.editpersonnel_award = result.data
      this.deletepersonnel_awarddialog = true
    },

    async deletepersonnel_awardubmit() {
      this.editpersonnel_award.ApiKey = this.ApiKey

      let result = await this.$http.post(
        'personnel_award.delete.php',
        this.editpersonnel_award
      )

      if (result.data.status === true) {
        Swal.fire({
          icon: 'success',
          title: 'ลบสำเร็จ',
          text: 'ลบผลงาน/รางวัลเรียบร้อยแล้ว',
          showConfirmButton: false,
          timer: 1500
        })
        this.personnel_awardsQueryAll()
      } else {
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถลบข้อมูลได้ กรุณาลองใหม่อีกครั้ง',
          showConfirmButton: true
        })
      }
      this.deletepersonnel_awarddialog = false
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Prompt:wght@300;400;500;600;700&display=swap');

.personnel-award-container {
  font-family: 'Prompt', sans-serif;
  padding: 24px;
  background: linear-gradient(180deg, #fafafa 0%, #f5f5f5 100%);
  min-height: 100vh;
}

/* Hero Header */
.hero-header {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 50%, #991b1b 100%);
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 10px 40px rgba(220, 38, 38, 0.3);
}

.hero-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M50 50c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10s-10-4.477-10-10 4.477-10 10-10zM10 10c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10S0 25.523 0 20s4.477-10 10-10zm10 8c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8zm40 40c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.hero-content {
  display: flex;
  align-items: center;
  padding: 32px;
  position: relative;
  z-index: 1;
  flex-wrap: wrap;
  gap: 16px;
}

.hero-icon-wrapper {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.hero-text {
  color: white;
}

.hero-title {
  font-size: 26px;
  font-weight: 600;
  margin: 0 0 6px 0;
  letter-spacing: -0.3px;
}

.hero-subtitle {
  font-size: 14px;
  opacity: 0.9;
  margin: 0;
  font-weight: 300;
}

.add-btn {
  background: white !important;
  color: #dc2626 !important;
  font-weight: 500;
  border-radius: 12px;
  padding: 0 28px !important;
  text-transform: none;
  letter-spacing: 0;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.add-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

/* Statistics Cards */
.stats-row {
  margin-top: -16px;
}

.stat-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-card-1 .stat-icon { background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%); }
.stat-card-2 .stat-icon { background: linear-gradient(135deg, #eab308 0%, #ca8a04 100%); }
.stat-card-3 .stat-icon { background: linear-gradient(135deg, #16a34a 0%, #15803d 100%); }
.stat-card-4 .stat-icon { background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); }

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
}

.stat-label {
  font-size: 13px;
  color: #6b7280;
  margin-top: 4px;
  font-weight: 400;
}

/* Main Table Card */
.main-table-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
}

.table-header {
  padding: 20px 24px;
  background: #fafafa;
}

.header-left {
  display: flex;
  align-items: center;
}

.header-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

.search-field {
  background: white;
}

.search-field >>> .v-input__slot {
  border-radius: 12px !important;
}

/* Data Table Styles */
.modern-table {
  font-family: 'Prompt', sans-serif;
}

.modern-table >>> thead th {
  background: #fef2f2 !important;
  color: #991b1b !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-bottom: 2px solid #fecaca !important;
  padding: 16px 12px !important;
}

.table-row {
  transition: all 0.2s ease;
}

.table-row:hover {
  background: #fffbfb !important;
}

.table-row td {
  padding: 16px 12px !important;
  border-bottom: 1px solid #f3f4f6 !important;
  font-size: 14px;
  color: #374151;
}

.performance-cell {
  display: flex;
  align-items: flex-start;
  max-width: 280px;
}

.performance-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.location-cell {
  display: flex;
  align-items: center;
}

.year-chip {
  font-weight: 600;
}

.level-chip {
  font-weight: 500;
  letter-spacing: 0;
}

.type-chip {
  font-weight: 500;
}

/* Action Buttons */
.action-btn {
  width: 34px !important;
  height: 34px !important;
  transition: all 0.2s ease;
}

.edit-btn {
  background: #fff7ed !important;
  color: #f97316 !important;
}

.edit-btn:hover {
  background: #ffedd5 !important;
  transform: scale(1.1);
}

.delete-btn {
  background: #fef2f2 !important;
  color: #dc2626 !important;
}

.delete-btn:hover {
  background: #fecaca !important;
  transform: scale(1.1);
}

/* Dialog Styles */
.modern-dialog {
  border-radius: 20px !important;
  overflow: hidden;
}

.dialog-card {
  border-radius: 20px !important;
  overflow: hidden;
}

.dialog-header {
  padding: 20px 24px;
  font-size: 18px;
  font-weight: 600;
}

.add-header {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
}

.edit-header {
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  color: white;
}

.delete-header {
  background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%);
  color: white;
}

.dialog-content {
  padding: 24px !important;
  max-height: 65vh;
  overflow-y: auto;
}

.form-section {
  background: #fafafa;
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 16px;
  border: 1px solid #f3f4f6;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.modern-input >>> .v-input__slot {
  background: white !important;
  border-radius: 10px !important;
}

.dialog-actions {
  padding: 16px 24px;
  background: #fafafa;
}

.cancel-btn {
  color: #6b7280 !important;
  font-weight: 500;
  text-transform: none;
}

.save-btn {
  border-radius: 10px;
  font-weight: 500;
  text-transform: none;
  padding: 0 24px !important;
}

/* Delete Dialog */
.delete-content {
  padding: 32px 24px !important;
}

.delete-icon-wrapper {
  margin-bottom: 16px;
}

.delete-message {
  font-size: 18px;
  font-weight: 500;
  color: #1f2937;
}

.delete-info-card {
  background: #fafafa;
  border-radius: 12px !important;
  border-color: #e5e7eb !important;
  text-align: left;
}

.delete-info-item {
  display: flex;
  align-items: flex-start;
}

.info-text {
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
}

.delete-info-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.delete-warning {
  font-size: 13px;
  color: #f97316;
}

.delete-confirm-btn {
  border-radius: 10px;
  font-weight: 500;
  text-transform: none;
}

/* Snackbar */
.modern-snackbar >>> .v-snack__wrapper {
  border-radius: 12px;
  font-family: 'Prompt', sans-serif;
}

/* Responsive */
@media (max-width: 960px) {
  .hero-content {
    padding: 24px;
  }

  .hero-icon-wrapper {
    width: 64px;
    height: 64px;
  }

  .hero-title {
    font-size: 20px;
  }

  .add-btn {
    width: 100%;
  }

  .performance-cell {
    max-width: 200px;
  }
}

@media (max-width: 600px) {
  .personnel-award-container {
    padding: 16px;
  }

  .hero-content {
    padding: 20px;
  }

  .stat-card {
    padding: 16px;
  }

  .stat-number {
    font-size: 24px;
  }
}

/* Animation */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stat-card {
  animation: fadeInUp 0.5s ease forwards;
  opacity: 0;
}

.stat-card:nth-child(1) { animation-delay: 0.1s; }
.stat-card:nth-child(2) { animation-delay: 0.2s; }
.stat-card:nth-child(3) { animation-delay: 0.3s; }
.stat-card:nth-child(4) { animation-delay: 0.4s; }

/* Custom scrollbar */
.dialog-content::-webkit-scrollbar {
  width: 6px;
}

.dialog-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.dialog-content::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 3px;
}

.dialog-content::-webkit-scrollbar-thumb:hover {
  background: #ccc;
}
</style>